import {
  Button,
  ButtonProps,
  ButtonGroup,
  CardProps,
  Flex,
  HStack,
  IconButton,
  Link,
  Text,
  Tooltip,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import { RiExternalLinkLine, RiFileCopyLine } from "@remixicon/react";
import { useLocalStorage } from "usehooks-ts";

import CodeBlock, {
  SupportedHighlightLanguage,
} from "@/common/components/CodeBlock";
import { DocumentationRefButton } from "@/common/components/DocumentationReference";
import SimpleSelect from "@/common/components/SimpleSelect";
import { OpenInCodeSampleSandboxButton } from "@/integration/components/code-sandbox/OpenInCodeSampleSandboxButton";
import { LinearConnectionIcon } from "@/integration/components/linear/LinearConnectionIcon";
import { useFlag } from "@reflag/react-sdk";
import { CodeSampleSandboxTemplate } from "@reflag/shared/codeSampleSandboxAPI";

export type CodeExampleType = {
  key: string;
  label: string;
  highlighterLanguage?: SupportedHighlightLanguage;
  code: string;
  docsSection?: string;
  template?: CodeSampleSandboxTemplate;
};

export type CodeExampleProps = {
  feature: { key: string; id: string };
  examples: CodeExampleType[];
  initialExample: CodeExampleType["key"];
  sharingPreface?: string;
  linearTitle?: string;
  linearMarkdown?: string;
  header?: string;
  languageStorageKey?: string;
  onLanguageChange?: (languageKey: string) => void;
  showOpenInSandboxButton?: boolean;
  showCopyButton?: boolean;
  showLinearButton?: boolean;
  showCopyForAIButton?: boolean;
  onCopyForAI?: () => void;
  copyForAILabel?: string;
  topActionsSize?: ButtonProps["size"];
  topActionsSpacing?: number;
  topActionsTop?: number;
  topActionsRight?: number;
  languageSelectMinW?: ButtonProps["minW"];
  showLanguageSelect?: boolean;
  showDocumentationButton?: boolean;
} & CardProps;

export function CodeExample({
  feature,
  examples,
  initialExample,
  sharingPreface,
  linearTitle,
  linearMarkdown,
  languageStorageKey = "code-example-language",
  onLanguageChange,
  showOpenInSandboxButton = true,
  showCopyButton = true,
  showLinearButton = true,
  showCopyForAIButton = false,
  onCopyForAI,
  copyForAILabel = "Copy for AI",
  topActionsSize = "xs",
  topActionsSpacing = 2,
  topActionsTop = 3,
  topActionsRight = 3,
  languageSelectMinW = "28",
  showLanguageSelect = true,
  showDocumentationButton = true,
  ...props
}: CodeExampleProps) {
  const {
    isEnabled: codeSandboxEnabled,
    config: { payload: codeSandboxConfig },
  } = useFlag("code-sample-sandbox");

  const [currentKey, setCurrentKey] = useLocalStorage(
    languageStorageKey,
    initialExample,
  );
  const currentExample = examples.find((x) => x.key === currentKey);

  if (!currentExample) return;

  const prefacedMarkdown = [
    `Code example for ${currentExample.label}:`,
    [
      "```" + currentExample.highlighterLanguage,
      currentExample.code,
      "```",
    ].join("\n"),
    `[More details in Reflag](${window.location.href})`,
  ]
    .filter(Boolean)
    .join("\n\n");

  const hasTopActions =
    showOpenInSandboxButton ||
    showCopyButton ||
    showLinearButton ||
    (showCopyForAIButton && !!onCopyForAI) ||
    showLanguageSelect;

  return (
    <Flex
      as="section"
      direction="column"
      fontSize="sm"
      gap={3}
      position="relative"
      px={1}
      py={2}
      w="full"
      {...props}
    >
      <CodeBlock language={currentExample.highlighterLanguage}>
        {currentExample.code}
      </CodeBlock>
      {hasTopActions && (
        <ButtonGroup
          position="absolute"
          right={topActionsRight}
          size={topActionsSize}
          spacing={topActionsSpacing}
          top={topActionsTop}
          variant="outline"
        >
          {showOpenInSandboxButton &&
            currentExample.template &&
            codeSandboxEnabled &&
            codeSandboxConfig &&
            codeSandboxConfig[currentExample.template] && (
              <OpenInCodeSampleSandboxButton
                featureId={feature.id}
                template={currentExample.template}
              />
            )}
          {showCopyButton && <CopyClipboardButton code={currentExample.code} />}
          {showLinearButton && (
            <TrackInLinearButton
              markdown={linearMarkdown ?? prefacedMarkdown}
              title={linearTitle ?? sharingPreface ?? "Reflag TODO"}
            />
          )}
          {showCopyForAIButton && onCopyForAI && (
            <Button size={topActionsSize} onClick={onCopyForAI}>
              {copyForAILabel}
            </Button>
          )}
          {showLanguageSelect && (
            <SimpleSelect
              minW={languageSelectMinW}
              size={topActionsSize}
              options={examples.map(({ key, label }) => ({
                value: key,
                label,
              }))}
              value={currentExample.key}
              onChange={(key) => {
                setCurrentKey(key);
                onLanguageChange?.(key);
              }}
            />
          )}
        </ButtonGroup>
      )}
      {showDocumentationButton && currentExample.docsSection && (
        <DocumentationRefButton
          bottom={3}
          position="absolute"
          right={3}
          section={currentExample.docsSection}
        />
      )}
    </Flex>
  );
}

export function CodeExampleAttributeBased() {
  return (
    <HStack fontSize="md" mx={4} my={3}>
      <Text>See</Text>
      <Link
        href="https://docs.reflag.com/supported-languages/overview"
        target="_blank"
      >
        <HStack spacing={1}>
          <Text>SDK documentation</Text>
          <RiExternalLinkLine size={14} />
        </HStack>
      </Link>
      <Text>for how to configure attribute-based flags</Text>
    </HStack>
  );
}

export function CopyClipboardButton({ code }: { code: string }) {
  const { hasCopied, onCopy } = useClipboard(code);
  const toast = useToast();

  function handleCopy() {
    onCopy();
    toast({
      title: "Code copied",
      status: "success",
    });
  }

  return (
    <Tooltip
      closeOnClick={false}
      label={hasCopied ? "Copied!" : "Copy to clipboard"}
    >
      <IconButton
        aria-label="Copy to clipboard"
        icon={<RiFileCopyLine size="14" />}
        onClick={handleCopy}
      />
    </Tooltip>
  );
}

export function TrackInLinearButton({
  title,
  markdown,
}: {
  title: string;
  markdown: string;
}) {
  const url = new URL("https://linear.app/new");

  url.searchParams.set("title", title);
  url.searchParams.set("description", markdown);

  return (
    <Tooltip label={"Create Linear issue"}>
      <IconButton
        aria-label="Create Linear issue"
        as={Link}
        href={url.toString()}
        icon={<LinearConnectionIcon boxSize={3.5} />}
        target="_blank"
      />
    </Tooltip>
  );
}
